import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { useDxtradeAccountMetricsWS } from '@pages/Trade/Portfolio/context/DxtradeAccountMetricsContext'
import { formatSignedUsd, getPnlColor } from '@pages/Trade/Portfolio/utils/formatters'
import { LowMarginAlert } from '@pages/Trade/TradingAccountInfo/components/LowMarginAlert'
import {
  SelectedTradingAccountProperty,
  SelectedTradingAccountPropertyProps,
} from '@pages/Trade/TradingAccountInfo/components/SelectedTradingAccount'
import { UsedMarginTooltipNew } from '@pages/Trade/TradingAccountInfo/components/UsedMarginTooltipNew'
import { useFundAccount } from '@pages/Trade/TradingAccountInfo/hooks/useFundAccount'
import { getLogoForAccount } from '@pages/Trade/TradingAccountInfo/utils/getLogoForAccount'
import { Account } from '@pages/Trade/types'
import { mapAccountMetrics } from '@pages/Trade/utils/helpers'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Caption1 } from '@ui/Typography'
import { Divider } from '@ui/components/Divider'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import styles from './TradingAccountPreview.module.scss'

interface TradingAccountPreviewProps {
  account: Account
  onClick?: (account: Account) => void
  isSelected?: boolean
  onDeposit?: () => void
}

export const TradingAccountPreview = ({
  account,
  onClick,
  isSelected,
  onDeposit,
}: TradingAccountPreviewProps) => {
  const { formatMoney, formatNumber } = useFormatNumber()
  const { t } = useTranslation()
  const makeDeposit = useFundAccount(account, onDeposit)

  const wsAccountKey = `default:${account.platformOverview.login}`
  const wsMetrics = useDxtradeAccountMetricsWS(wsAccountKey)
  const metrics = mapAccountMetrics({
    ws: wsMetrics,
    rest: account.platformOverview,
  })
  const pnlColor = getPnlColor(metrics.openPL)
  const pnlFormatted =
    metrics.openPL !== null ? formatSignedUsd(metrics.openPL, account.currency.id) : null
  const isLowMargin = metrics.marginLevel !== null && metrics.marginLevel < 100
  const properties: SelectedTradingAccountPropertyProps[] = [
    {
      title: t('Markets.Equity'),
      tooltip: t('Markets.EquityTooltip'),
      accountMetricsValue:
        metrics.equity !== null ? formatMoney(metrics.equity, account.currency.id) : '—',
      color: isLowMargin ? 'text-danger-high-em' : undefined,
    },
    {
      title: t('Markets.FreeMargin'),
      tooltip: t('Markets.FreeMarginTooltip'),
      accountMetricsValue:
        metrics.marginFree !== null ? formatMoney(metrics.marginFree, account.currency.id) : '—',
    },
    {
      title: t('Markets.UsedMargin'),
      tooltip:
        metrics.marginLevel !== null ? <UsedMarginTooltipNew /> : t('Markets.UsedMarginTooltip'),
      accountMetricsValue:
        metrics.usedMargin !== null ? (
          metrics.marginLevel !== null ? (
            <>
              {formatMoney(metrics.usedMargin, account.currency.id)}{' '}
              <Caption1 medium color={isLowMargin ? 'text-danger-high-em' : 'text-high-em'}>
                ({formatNumber(metrics.marginLevel, { minDecimalPlaces: 0, maxDecimalPlaces: 0 })}%)
              </Caption1>
            </>
          ) : (
            formatMoney(metrics.usedMargin, account.currency.id)
          )
        ) : (
          '—'
        ),
    },
  ]
  return (
    <div>
      {isSelected && isLowMargin && <LowMarginAlert onDeposit={makeDeposit} />}
      <div
        onClick={() => onClick?.(account)}
        className={classNames(
          styles.tradingAccountPreviewContainer,
          isSelected && isLowMargin && styles.tradingAccountPreviewContainerWithAlert
        )}
      >
        <Box flex direction='column' gap='x12'>
          <Box flex direction='row' justify='space-between' align='center'>
            <Box flex direction='row' justify='space-between' align='center' gap='x12'>
              <div className={styles.avatar}>
                <div className={styles.logoWrapper}>{getLogoForAccount(account)}</div>
              </div>

              <div>
                <Caption1 color='text-med-em'> {account.nickname || account.name}</Caption1>

                <Box direction='row' flex align='center' gap='x4'>
                  <Body1>
                    {metrics.balance !== null ? (
                      <SensitiveData>
                        {formatMoney(metrics.balance, account.currency.id)}
                      </SensitiveData>
                    ) : (
                      ''
                    )}
                  </Body1>

                  {pnlFormatted && (
                    <Caption1 color={pnlColor} className={styles.numericValue}>
                      <SensitiveData>{pnlFormatted}</SensitiveData>
                    </Caption1>
                  )}
                </Box>
              </div>
            </Box>

            {isSelected && <CircleCheckIcon color='text-purple-high-em' />}
          </Box>

          {isSelected && (
            <>
              <Divider.Horizontal />
              <div className={styles.propertiesGrid}>
                {properties.map((property, i) => (
                  <SelectedTradingAccountProperty
                    key={i}
                    title={property.title}
                    tooltip={property.tooltip}
                    accountMetricsValue={property.accountMetricsValue}
                    color={property.color}
                    onClick={property.onClick}
                  />
                ))}
              </div>
            </>
          )}
        </Box>
      </div>
    </div>
  )
}
